// frontend/src/shared/services/authRefreshService.ts

/**
 * 🔐 AUTH REFRESH SERVICE - Gestión de Refresh Tokens (Frontend)
 *
 * Responsabilidad: Manejar el refresh automático de access tokens
 *
 * Características:
 * - Singleton pattern: Una sola instancia en toda la app
 * - Queue de peticiones: Evita múltiples refreshes simultáneos
 * - Thread-safe: Previene race conditions
 * - Auto-logout: Si el refresh falla, cierra sesión automáticamente
 *
 * Flujo:
 * 1. Request falla con 401
 * 2. Si no hay refresh en progreso → iniciar refresh
 * 3. Si hay refresh en progreso → esperar a que termine
 * 4. Retry request original con nuevo token
 * 5. Si refresh falla → logout automático
 *
 * @see Phase 1, Task 1.2 - ROADMAP_TO_10.md (Frontend - Día 2)
 */

import { logger } from '../utils/logger';
import { ROUTE_AUTH_REFRESH } from '../../config/routes/routeConfig';

/**
 * Callback para logout cuando el refresh falla
 */
type LogoutCallback = () => void | Promise<void>;

/**
 * Estado del refresh
 */
interface RefreshState {
	isRefreshing: boolean;
	refreshPromise: Promise<boolean> | null;
	logoutCallback: LogoutCallback | null;
}

/**
 * Servicio singleton para gestionar refresh de tokens
 */
class AuthRefreshService {
	private state: RefreshState = {
		isRefreshing: false,
		refreshPromise: null,
		logoutCallback: null,
	};

	/**
	 * Configura el callback de logout
	 * Se llamará automáticamente si el refresh falla
	 *
	 * @param callback - Función de logout
	 */
	public setLogoutCallback(callback: LogoutCallback): void {
		this.state.logoutCallback = callback;
	}

	/**
	 * Verifica si hay un refresh en progreso
	 *
	 * @returns true si está refrescando
	 */
	public isRefreshing(): boolean {
		return this.state.isRefreshing;
	}

	/**
	 * Refresca el access token llamando al endpoint de refresh
	 *
	 * Implementa patrón singleton con promise caching:
	 * - Si no hay refresh en progreso → inicia uno nuevo
	 * - Si hay refresh en progreso → retorna la promesa existente
	 *
	 * Esto previene race conditions cuando múltiples requests
	 * fallan simultáneamente con 401.
	 *
	 * @returns Promise<boolean> - true si el refresh fue exitoso
	 */
	public async refreshAccessToken(): Promise<boolean> {
		// Si ya hay un refresh en progreso, esperar a que termine
		if (this.state.isRefreshing && this.state.refreshPromise) {
			return this.state.refreshPromise;
		}

		// Iniciar nuevo refresh
		this.state.isRefreshing = true;

		// Crear promesa de refresh y cachearla
		this.state.refreshPromise = this.performRefresh();

		// Esperar resultado
		const success = await this.state.refreshPromise;

		// Limpiar estado
		this.state.isRefreshing = false;
		this.state.refreshPromise = null;

		return success;
	}

	/**
	 * Realiza el refresh token request
	 *
	 * @returns true si el refresh fue exitoso
	 */
	private async performRefresh(): Promise<boolean> {
		try {
			const response = await fetch(ROUTE_AUTH_REFRESH, {
				method: 'POST',
				credentials: 'include', // Envía cookies httpOnly
				headers: {
					'Content-Type': 'application/json',
				},
			});

			if (response.ok) {
				return true;
			}

			// Refresh falló (token inválido/expirado)
			logger.warn('Token refresh failed', {
				status: response.status,
				statusText: response.statusText,
			});

			// Llamar logout callback si está configurado
			await this.handleRefreshFailure();

			return false;
		} catch (error) {
			logger.error('Token refresh request failed', {
				error: error instanceof Error ? error.message : String(error),
			});

			// Llamar logout callback en caso de error de red
			await this.handleRefreshFailure();

			return false;
		}
	}

	/**
	 * Maneja el caso cuando el refresh falla
	 * Ejecuta el callback de logout si está configurado
	 */
	private async handleRefreshFailure(): Promise<void> {
		if (this.state.logoutCallback) {
			try {
				await this.state.logoutCallback();
			} catch (error) {
				logger.error('Logout callback failed', {
					error: error instanceof Error ? error.message : String(error),
				});
			}
		}
	}

	/**
	 * Reinicia el estado del servicio
	 * Útil para testing
	 */
	public reset(): void {
		this.state = {
			isRefreshing: false,
			refreshPromise: null,
			logoutCallback: null,
		};
	}
}

// Exportar instancia singleton
export const authRefreshService = new AuthRefreshService();

// Exportar también la clase para testing
export { AuthRefreshService };
